/**
 * Hook for fetching pipeline objects from the backend schema.
 *
 * Pipelines are derived from stage seed data grouped by pipeline name.
 * Each pipeline has a name, label, and list of stages.
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';

import { isValidEntityTypeName } from '@/lib/crm/core/types';
import {
  getObjectSeq,
  upsertSummaryObjects,
} from '@/lib/crm/store/crmObjectStore';
import { useStoreObjectsMap } from '@/lib/crm/store/hooks';
import { apiCall } from '@/lib/http/client';
import {
  getPersistedPipelines,
  setPersistedPipelines,
} from '@/lib/localStorage/persistedPipelines';
import type { CrmObject, PropertyValue } from '@/types/crm';

export interface PipelineStageSchema {
  stage_name: string;
  label: string;
  description: string;
  entity_id: string | null;
  is_terminal: boolean;
  properties: {
    probability?: number;
    order: number;
    color?: string | null;
  };
}

export interface Pipeline {
  pipeline_name: string;
  label: string;
  entity_type_name: string;
  base_entity_type_name?: string;
  description: string;
  properties: Record<string, unknown>;
  stages: PipelineStageSchema[];
  is_default: boolean;
  entity_id: string | null;
}

async function fetchPipelines(
  orgId: number,
  entityType?: string
): Promise<Pipeline[]> {
  let url = `/api/orgs/${orgId}/crm-events/schemas/pipelines/`;
  if (entityType) {
    url += `?entity_type=${entityType}`;
  }
  return apiCall<Pipeline[]>(url);
}

/**
 * Hook to fetch all pipelines from the backend schema.
 *
 * @param orgId - Org ID
 * @param entityType - Optional entity type to filter pipelines
 * @returns Pipelines, loading state, error, and helper to get the default pipeline
 *
 * @example
 * const { pipelines, isLoading, getDefaultPipeline } = usePipelines(orgId);
 * const defaultPipeline = getDefaultPipeline();
 *
 * @example
 * // Filter by entity type
 * const { pipelines } = usePipelines(orgId, 'org_project');
 */
export function usePipelines(orgId: number | undefined, entityType?: string) {
  const {
    data: pipelines = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['pipelines', orgId, entityType],
    queryFn: async () => {
      if (!orgId) {
        return [];
      }
      return fetchPipelines(orgId, entityType);
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes cache time
    initialData: () =>
      entityType === undefined ? getPersistedPipelines(orgId) : undefined,
    initialDataUpdatedAt: () => 0,
  });

  useEffect(() => {
    if (!orgId || pipelines.length === 0) {
      return;
    }
    const pipelineObjects: CrmObject[] = [];
    for (const p of pipelines) {
      if (p.entity_id == null) {
        continue;
      }
      // Reject malformed entity_type_name values (e.g., *_timeline) before
      // they reach the store. A force-cast would let invalid types in and
      // poison downstream consumers that trust the runtime type.
      if (!isValidEntityTypeName(p.entity_type_name)) {
        continue;
      }
      // The schema endpoint doesn't return a per-pipeline `last_event_seq`,
      // so we have to seed with seq=0. If a real WS event has already
      // bumped the store entry to seq>0, our seed would either be silently
      // rejected by the seq guard (existing >= 0) or — worse — overlay a
      // stale label on top of a fresher WS-applied one. Skip seeding for
      // any pipeline the store already has WS-derived state for; the
      // enrichment below picks up the WS data via the normal store path.
      const existingSeq = getObjectSeq(p.entity_id);
      if (existingSeq !== undefined && existingSeq > 0) {
        continue;
      }
      // Normalize each property to the PropertyValue shape. The schema API
      // returns values in `{value: x}` form, but we can't trust that without
      // checking — wrap raw values defensively so the store always sees a
      // valid PropertyValue.
      const properties: Record<string, PropertyValue> = {
        ahoy_name: { value: p.label },
      };
      for (const [key, raw] of Object.entries(p.properties)) {
        // Inverse of getPropertyValue: guards before wrapping a raw value
        // INTO {value} for the store — do not unwrap here
        if (
          raw !== null &&
          typeof raw === 'object' &&
          'value' in (raw as object)
        ) {
          properties[key] = raw as PropertyValue;
        } else {
          properties[key] = { value: raw };
        }
      }
      pipelineObjects.push({
        id: p.entity_id,
        org_id: orgId,
        entity_id: p.entity_id,
        entity_type_name: p.entity_type_name,
        properties,
        last_event_seq: 0,
        associations: [],
        deleted_at: null,
      });
    }
    if (pipelineObjects.length > 0) {
      upsertSummaryObjects(pipelineObjects);
    }
  }, [pipelines, orgId]);

  useEffect(() => {
    if (entityType !== undefined) {
      return;
    }
    if (!orgId) {
      return;
    }
    if (!pipelines) {
      return;
    }
    // Persist even empty arrays so a deleted-all-pipelines state clears the
    // stale localStorage snapshot instead of silently rehydrating it on
    // next boot.
    setPersistedPipelines(orgId, pipelines);
  }, [pipelines, orgId, entityType]);

  const pipelineEntityIds = useMemo(
    () =>
      pipelines
        .filter(
          (p): p is Pipeline & { entity_id: string } => p.entity_id != null
        )
        .map(p => p.entity_id),
    [pipelines]
  );

  const storeMap = useStoreObjectsMap(pipelineEntityIds);

  const enrichedPipelines = useMemo(
    () =>
      pipelines.flatMap(p => {
        if (!p.entity_id) {
          return [p];
        }
        const stored = storeMap.get(p.entity_id);
        if (!stored || stored.last_event_seq <= 0) {
          return [p];
        }
        // Drop pipelines that the store knows have been deleted (e.g. via a
        // WS `ahoy_object_deleted` event). Without this, the deleted pipeline
        // stays visible in pickers/lists until the React Query refetch runs,
        // which is up to staleTime (5 min) away.
        if (stored.deleted_at) {
          return [];
        }
        const newLabel = (
          stored.properties?.ahoy_name as { value?: string } | undefined
        )?.value;
        // Use a typeof check rather than truthiness so a deliberately empty
        // label ("") from the store overrides a stale fetched value.
        return [typeof newLabel === 'string' ? { ...p, label: newLabel } : p];
      }),
    [pipelines, storeMap]
  );

  /**
   * Returns the pipeline marked as default (is_default === true), or the first pipeline overall.
   */
  const getDefaultPipeline = useCallback((): Pipeline | undefined => {
    if (enrichedPipelines.length === 0) {
      return undefined;
    }
    return enrichedPipelines.find(p => p.is_default) || enrichedPipelines[0];
  }, [enrichedPipelines]);

  return {
    pipelines: enrichedPipelines,
    isLoading,
    error,
    refetch,
    getDefaultPipeline,
  };
}
